'use client';
// MVP_AUTH_MODE: Username + Password registration
// Email: OPTIONAL — stored if provided, never required
// ATOMIC: Registration uses complete_member_registration RPC for full atomicity.
// On any failure, cleanup_failed_registration removes orphaned auth.users record.
// PERSISTENT CREDENTIALS PANEL: Registration success shows a persistent panel
// that remains until explicitly dismissed — never auto-hides.
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, Phone, Upload, Check, ChevronRight, ChevronLeft, AlertCircle, Info, Link2, User, CreditCard, Copy, Share2, X, CheckCircle2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import GoldButton from '@/components/ui/GoldButton';
import { createClient } from '@/lib/supabase/client';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  idNumber: string;
  passportNumber?: string;
  idType: 'sa_id' | 'passport';
  password: string;
  confirmPassword: string;
  elitePlan: string;
  referralCode: string;
  agreeTerms: boolean;
  eftProof?: FileList;
}

interface RegistrationResult {
  username: string;
  email: string;
  phone: string;
  referralCode: string;
  referralLink: string;
  paymentReference: string;
}

const elitePlans = [
  { value: 'elite_1000', label: 'Elite 1000', price: 'R 1,000', desc: 'Entry level — unlock binary network' },
  { value: 'elite_2000', label: 'Elite 2000', price: 'R 2,000', desc: 'Level 2 — increased referral rewards' },
  { value: 'elite_4000', label: 'Elite 4000', price: 'R 4,000', desc: 'Level 3 — Team Leader promotion eligible' },
  { value: 'elite_8000', label: 'Elite 8000', price: 'R 8,000', desc: 'Level 4 — senior network rewards' },
  { value: 'elite_16000', label: 'Elite 16000', price: 'R 16,000', desc: 'Level 5 — executive tier' },
];

const PLAN_PRICE: Record<string, number> = {
  elite_1000: 1000, elite_2000: 2000, elite_4000: 4000,
  elite_8000: 8000, elite_16000: 16000,
};

const SITE_URL = (() => {
  const url = process.env.NEXT_PUBLIC_SITE_URL;
  if (!url || url.trim() === '' || url === 'https://your-production-domain.example') {
    if (process.env.NODE_ENV === 'production') {
      console.error('[MBG Nexus] NEXT_PUBLIC_SITE_URL is not configured. Referral links will be unavailable.');
      return '';
    }
    // Development-only fallback — never used in production
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  return url;
})();

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

// ─── PERSISTENT REGISTRATION CREDENTIALS PANEL ──────────────────────────────
function RegistrationCompletePanel({
  result,
  onDismiss,
  onContinue,
}: {
  result: RegistrationResult;
  onDismiss: () => void;
  onContinue: () => void;
}) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Join MBG Nexus',
      text: `Join MBG Nexus using my referral link: ${result.referralLink}`,
      url: result.referralLink,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      await copyToClipboard(result.referralLink, 'share');
    }
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Join me on MBG Nexus — South Africa's digital community participation platform!\n\nUse my personal referral link to register:\n${result.referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const copyLoginDetails = async () => {
    const details = [
      `MBG Nexus Login Details`,
      `Username: ${result.username}`,
      `Email: ${result.email || 'Not provided'}`,
      `Phone: ${result.phone}`,
      `Referral Code: ${result.referralCode}`,
      `Referral Link: ${result.referralLink}`,
      `Payment Reference: ${result.paymentReference}`,
    ].join('\n');
    await copyToClipboard(details, 'login_details');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg glass-card rounded-3xl border border-gold/30 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="gold-gradient p-6 text-center relative">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Registration Complete!</h2>
          <p className="text-white/80 text-sm">Save your details before continuing</p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Important notice */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-400/90">
              <strong>Important:</strong> Save your username and referral code now. This panel will remain visible until you dismiss it. Your details are also accessible from My Profile and the Referral Tree page.
            </p>
          </div>

          {/* Credentials */}
          <div className="space-y-3">
            {/* Username */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Permanent Username</p>
                <p className="text-base font-bold text-foreground font-mono">{result.username}</p>
              </div>
              <button
                onClick={() => copyToClipboard(result.username, 'username')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${copiedField === 'username' ? 'bg-green-400/20 text-green-400' : 'btn-gold'}`}
              >
                {copiedField === 'username' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedField === 'username' ? 'Copied' : 'Copy'}
              </button>
            </div>

            {/* Email */}
            {result.email && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Registered Email</p>
                  <p className="text-sm font-medium text-foreground">{result.email}</p>
                </div>
              </div>
            )}

            {/* Phone */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Registered Phone</p>
                <p className="text-sm font-medium text-foreground">{result.phone}</p>
              </div>
            </div>

            {/* Referral Code */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-gold/5 border border-gold/20">
              <div>
                <p className="text-[10px] text-gold uppercase tracking-wider font-semibold">Your Referral Code</p>
                <p className="text-xl font-bold text-gold font-mono tracking-wider">{result.referralCode}</p>
              </div>
              <button
                onClick={() => copyToClipboard(result.referralCode, 'referral_code')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${copiedField === 'referral_code' ? 'bg-green-400/20 text-green-400' : 'btn-gold'}`}
              >
                {copiedField === 'referral_code' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedField === 'referral_code' ? 'Copied' : 'Copy'}
              </button>
            </div>

            {/* Referral Link */}
            <div className="p-3 rounded-xl bg-surface-2 border border-border">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Personal Referral Link</p>
              <p className="text-xs text-foreground font-mono break-all mb-2">{result.referralLink}</p>
              <button
                onClick={() => copyToClipboard(result.referralLink, 'referral_link')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all w-full justify-center ${copiedField === 'referral_link' ? 'bg-green-400/20 text-green-400' : 'btn-outline-gold'}`}
              >
                {copiedField === 'referral_link' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedField === 'referral_link' ? 'Link Copied!' : 'Copy Referral Link'}
              </button>
            </div>

            {/* Payment Reference */}
            {result.paymentReference && (
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-2 border border-border">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Payment Reference</p>
                  <p className="text-sm font-bold text-foreground font-mono">{result.paymentReference}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(result.paymentReference, 'payment_ref')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${copiedField === 'payment_ref' ? 'bg-green-400/20 text-green-400' : 'btn-outline-gold'}`}
                >
                  {copiedField === 'payment_ref' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedField === 'payment_ref' ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 space-y-3">
          {/* Share row */}
          <div className="flex gap-2">
            <button
              onClick={() => copyToClipboard(result.referralLink, 'copy_link')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all border ${copiedField === 'copy_link' ? 'bg-green-400/20 text-green-400 border-green-400/30' : 'btn-outline-gold'}`}
            >
              {copiedField === 'copy_link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy Link
            </button>
            <button
              onClick={handleWhatsApp}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium bg-green-600 hover:bg-green-500 text-white transition-all"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </button>
            <button
              onClick={handleShare}
              aria-label="Share invite"
              title="Share invite"
              className="flex items-center justify-center w-11 h-11 rounded-xl btn-outline-gold transition-all"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={copyLoginDetails}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all border ${copiedField === 'login_details' ? 'bg-green-400/20 text-green-400 border-green-400/30' : 'border-border text-muted-foreground hover:text-foreground hover:border-gold/30'}`}
          >
            {copiedField === 'login_details' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copiedField === 'login_details' ? 'Details Copied!' : 'Copy All Login Details'}
          </button>

          <GoldButton onClick={onContinue} className="w-full justify-center">
            <UserCheck className="w-4 h-4" />
            Continue to Member Portal
          </GoldButton>

          <button
            onClick={onDismiss}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-3 h-3" />
            Dismiss (I have saved my details)
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── REFERRAL SPONSOR BANNER ─────────────────────────────────────────────────
function ReferralSponsorBanner({ sponsorName }: { sponsorName: string }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-gold/10 border border-gold/20 mb-4">
      <UserCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" />
      <p className="text-xs text-gold/90">
        You were invited by <strong>{sponsorName}</strong>. Your referral relationship will be permanently recorded upon registration.
      </p>
    </div>
  );
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResult | null>(null);
  const [sponsorName, setSponsorName] = useState<string | null>(null);
  const [referralCodeFromUrl, setReferralCodeFromUrl] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({ defaultValues: { elitePlan: 'elite_1000', referralCode: '', idType: 'sa_id' } });

  const password = watch('password');
  const selectedPlan = watch('elitePlan');
  const idType = watch('idType');

  // ── Auto-detect referral code from URL ?ref= param ──────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode && refCode.trim() !== '') {
      const code = refCode.trim().toUpperCase();
      setReferralCodeFromUrl(code);
      setValue('referralCode', code);

      // Validate the referral code and show sponsor display name only.
      // SECURITY: sponsor_id (internal member UUID) must NOT be used client-side.
      // The sponsor UUID is resolved server-side inside complete_member_registration().
      // Only sponsor_display_name is used here for UI confirmation purposes.
      const supabase = createClient();
      supabase.rpc('resolve_referral_code', { p_referral_code: code }).then(({ data }) => {
        if (data?.valid && data?.sponsor_display_name) {
          setSponsorName(data.sponsor_display_name);
          // Intentionally NOT storing data.sponsor_id — internal UUID must not
          // be held or transmitted by the client. The RPC resolves it server-side.
        }
      }).catch(() => {
        // Non-fatal — referral code validation failure doesn't block registration
      });
    }
  }, [setValue]);

  const stepFields: Record<number, (keyof RegisterFormData)[]> = {
    1: ['firstName', 'lastName', 'phone', 'idNumber', 'password', 'confirmPassword'],
    2: ['elitePlan'],
    3: [],
  };

  const handleNext = async () => {
    const valid = await trigger(stepFields[step]);
    if (valid) setStep(step + 1);
  };

  const onSubmit = async (data: RegisterFormData) => {
    const supabase = createClient();
    let authUserId: string | null = null;

    try {
      // ── STEP 1: Build synthetic email for Supabase Auth ──────────────────
      const syntheticEmail = data.email && data.email.trim() !== ''
        ? data.email.trim()
        : `${data.phone.replace(/\D/g, '')}@mbgnexus.local`;

      // ── STEP 2: Create auth user ─────────────────────────────────────────
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: syntheticEmail,
        password: data.password,
        options: {
          data: {
            full_name: `${data.firstName} ${data.lastName}`.trim(),
            // NOTE: 'role' is intentionally NOT set in JWT metadata.
            // Application roles are resolved exclusively from the DB
            // (office_accounts → member_profiles → null) via get_authenticated_user_role().
            // Setting role in JWT metadata would create a JWT-authority bypass
            // that the new RLS architecture explicitly prohibits.
          },
          emailRedirectTo: undefined,
        },
      });

      if (authError) {
        if (authError.message.toLowerCase().includes('already registered') ||authError.message.toLowerCase().includes('already been registered')) {
          toast.error('This phone number is already registered. Please log in or use a different number.');
        } else {
          toast.error(`Registration failed: ${authError.message}`);
        }
        return;
      }

      authUserId = authData.user?.id ?? null;
      if (!authUserId) {
        toast.error('Registration failed: could not create account. Please try again.');
        return;
      }

      // ── STEP 3: Upload EFT proof (optional — non-fatal) ──────────────────
      let filePath: string | null = null;
      if (uploadedFile) {
        const fileExt = uploadedFile.name.split('.').pop();
        filePath = `eft-proofs/${authUserId}/${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('eft-proofs')
          .upload(filePath, uploadedFile, { upsert: false });
        if (uploadError) {
          console.warn('EFT proof upload failed (non-fatal):', uploadError.message);
          filePath = null;
        }
      }

      // ── STEP 4: Atomic registration via RPC ──────────────────────────────
      // complete_member_registration() is the ONLY authorised path for creating
      // member_profiles records. Direct INSERT into member_profiles is prohibited.
      // The RPC enforces: auth.uid() === p_auth_user_id (cannot register another user).
      // The RPC handles: wallet creation, payment reference, referral processing,
      // tree placement, audit logging, and all registration validation atomically.
      const { data: regResult, error: regError } = await supabase.rpc(
        'complete_member_registration',
        {
          p_auth_user_id: authUserId,
          p_first_name: data.firstName,
          p_last_name: data.lastName,
          p_phone: data.phone,
          p_id_number: data.idNumber,
          p_plan_tier: data.elitePlan,
          p_referral_code: data.referralCode || null,
          p_email: data.email && data.email.trim() !== '' ? data.email.trim() : null,
          p_eft_proof_path: filePath,
          p_province: null,
        }
      );

      if (regError || !regResult?.success) {
        // Rollback: Remove orphaned auth user
        console.error('Registration RPC failed:', regError?.message || regResult?.error);
        try {
          await supabase.rpc('cleanup_failed_registration', {
            p_auth_user_id: authUserId,
            p_phone: data.phone,
          });
        } catch (cleanupErr) {
          console.warn('Cleanup RPC failed (auth user may be orphaned):', cleanupErr);
        }
        const errMsg = regError?.message || regResult?.error || 'Unknown error during registration';
        // Surface plan_tier validation errors clearly
        if (errMsg.toLowerCase().includes('plan') || errMsg.toLowerCase().includes('tier')) {
          toast.error(`Invalid plan selection: ${errMsg}. Please select a valid Elite plan.`);
        } else {
          toast.error(`Registration failed: ${errMsg}. Please try again.`);
        }
        authUserId = null;
        return;
      }

      // ── STEP 5: Check tree placement result ──────────────────────────────
      // tree_placed accurately reflects whether a tree row was actually created.
      // A false value means the member is registered but pending tree placement.
      if (regResult.tree_placed === false) {
        console.warn('[RegisterForm] Tree placement pending — member registered but not yet placed in tree.');
      }

      // ── STEP 6: Show persistent credentials panel ─────────────────────────
      const assignedUsername = regResult.username || 'your assigned username';
      const assignedReferralCode = regResult.referral_code || assignedUsername;
      const realEmail = data.email && data.email.trim() !== '' ? data.email.trim() : '';

      setRegistrationResult({
        username: assignedUsername,
        email: realEmail,
        phone: data.phone,
        referralCode: assignedReferralCode,
        referralLink: `${SITE_URL}/sign-up-login-screen?ref=${assignedReferralCode}`,
        paymentReference: regResult.payment_reference || '',
      });

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.error('Registration exception:', msg);
      if (authUserId) {
        try {
          const supabaseCleanup = createClient();
          await supabaseCleanup.rpc('cleanup_failed_registration', {
            p_auth_user_id: authUserId,
            p_phone: data.phone,
          });
        } catch (cleanupErr) {
          console.warn('Exception cleanup failed:', cleanupErr);
        }
      }
      toast.error(`Registration error: ${msg}. Please try again.`);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setUploadedFile(file);
  };

  const handleContinueToPortal = () => {
    window.location.href = '/member-portal';
  };

  const handleDismiss = () => {
    setRegistrationResult(null);
    window.location.href = '/member-portal';
  };

  const selectedPlanData = elitePlans.find((p) => p.value === selectedPlan);

  // Show persistent credentials panel after successful registration
  if (registrationResult) {
    return (
      <RegistrationCompletePanel
        result={registrationResult}
        onDismiss={handleDismiss}
        onContinue={handleContinueToPortal}
      />
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Join MBG Nexus</h2>
        <p className="text-sm text-muted-foreground">Create your community membership account</p>
      </div>

      {/* Referral sponsor confirmation */}
      {sponsorName && <ReferralSponsorBanner sponsorName={sponsorName} />}

      {/* MVP Auth Mode Notice */}
      <div className="flex items-start gap-3 p-3 rounded-xl bg-gold/10 border border-gold/20 mb-6">
        <Info className="w-4 h-4 text-gold shrink-0 mt-0.5" />
        <p className="text-xs text-gold/90">
          Your username will be automatically generated in the format <span className="font-mono font-bold">MBG000001</span>. Use it to log in. Email is optional.
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={`step-${s}`}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all duration-300 ${
              step > s ? 'gold-gradient text-primary-foreground' :
              step === s ? 'border-2 border-gold text-gold' : 'border-2 border-border text-muted-foreground'
            }`}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            {s < 3 && <div className={`flex-1 h-0.5 transition-all duration-300 ${step > s ? 'bg-gold' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mb-6 -mt-4">
        <span>Personal Details</span>
        <span>Elite Plan</span>
        <span>EFT Proof</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Personal Details */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">First Name <span className="text-danger">*</span></label>
                <input type="text" placeholder="First name" className="input-field" {...register('firstName', { required: 'Required' })} />
                {errors.firstName && <p className="mt-1 text-xs text-danger">{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Last Name <span className="text-danger">*</span></label>
                <input type="text" placeholder="Last name" className="input-field" {...register('lastName', { required: 'Required' })} />
                {errors.lastName && <p className="mt-1 text-xs text-danger">{errors.lastName.message}</p>}
              </div>
            </div>

            {/* Email — Optional */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-muted-foreground font-normal text-xs">(optional)</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="email" placeholder="your@email.com (optional)" className="input-field pl-10"
                  {...register('email', {
                    validate: (val) => {
                      if (!val || val.trim() === '') return true;
                      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || 'Invalid email format';
                    }
                  })} />
              </div>
              {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
              <p className="mt-1 text-xs text-muted-foreground">Email is optional. You will log in using your auto-generated MBG username.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Mobile Number <span className="text-danger">*</span></label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="tel" placeholder="+27 XX XXX XXXX" className="input-field pl-10" {...register('phone', { required: 'Mobile number required' })} />
              </div>
              {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone.message}</p>}
            </div>

            {/* ID Type Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Identity Document <span className="text-danger">*</span></label>
              <div className="flex gap-3 mb-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-xl border cursor-pointer transition-all text-sm ${idType === 'sa_id' ? 'border-gold/50 bg-gold/5 text-gold' : 'border-border text-muted-foreground hover:border-gold/20'}`}>
                  <input type="radio" value="sa_id" className="hidden" {...register('idType')} />
                  <CreditCard className="w-4 h-4" /> SA ID Number
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-xl border cursor-pointer transition-all text-sm ${idType === 'passport' ? 'border-gold/50 bg-gold/5 text-gold' : 'border-border text-muted-foreground hover:border-gold/20'}`}>
                  <input type="radio" value="passport" className="hidden" {...register('idType')} />
                  <CreditCard className="w-4 h-4" /> Passport
                </label>
              </div>
              {idType === 'sa_id' ? (
                <>
                  <p className="text-xs text-muted-foreground mb-2">Your 13-digit South African ID number</p>
                  <input type="text" placeholder="13-digit SA ID number" maxLength={13} className="input-field font-mono"
                    {...register('idNumber', { required: 'SA ID required', minLength: { value: 13, message: '13 digits required' }, maxLength: { value: 13, message: '13 digits required' } })} />
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">Your passport number (foreign members)</p>
                  <input type="text" placeholder="Passport number" className="input-field font-mono uppercase"
                    {...register('idNumber', { required: 'Passport number required', minLength: { value: 6, message: 'Invalid passport number' } })} />
                </>
              )}
              {errors.idNumber && <p className="mt-1 text-xs text-danger">{errors.idNumber.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Referral Code <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="text" placeholder="MBG000001" className="input-field pl-10 font-mono uppercase"
                  {...register('referralCode')} />
              </div>
              {referralCodeFromUrl && (
                <p className="mt-1 text-xs text-gold">✓ Referral code pre-filled from your invitation link</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password <span className="text-danger">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" className="input-field pl-10 pr-10"
                  autoComplete="new-password"
                  {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 characters' } })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Confirm Password <span className="text-danger">*</span></label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type={showConfirm ? 'text' : 'password'} placeholder="••••••••" className="input-field pl-10 pr-10"
                  autoComplete="new-password"
                  {...register('confirmPassword', { required: 'Required', validate: (val) => val === password || 'Passwords do not match' })} />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
            </div>

            <GoldButton type="button" onClick={handleNext} className="w-full justify-center mt-2">
              Continue to Plan Selection
              <ChevronRight className="w-4 h-4" />
            </GoldButton>
          </div>
        )}

        {/* Step 2: Elite Plan Selection */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Select Your Elite Plan</h3>
              <p className="text-sm text-muted-foreground mb-4">Plans must be completed sequentially. You start at Elite 1000.</p>
            </div>

            <div className="space-y-3">
              {elitePlans.map((plan) => {
                const isSelected = selectedPlan === plan.value;
                return (
                  <label key={`plan-${plan.value}`}
                    className={`flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
                      isSelected ? 'border-gold/50 bg-gold/5 gold-border-glow' : 'border-border bg-surface-2 hover:border-gold/20'
                    }`}>
                    <input type="radio" value={plan.value} className="hidden" {...register('elitePlan')} />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isSelected ? 'border-gold' : 'border-border'}`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground">{plan.label}</p>
                        <p className="text-sm font-bold text-gold tabular-nums">{plan.price}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{plan.desc}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex gap-3 mt-6">
              <GoldButton type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 justify-center">
                <ChevronLeft className="w-4 h-4" /> Back
              </GoldButton>
              <GoldButton type="button" onClick={handleNext} className="flex-1 justify-center">
                Continue to Payment <ChevronRight className="w-4 h-4" />
              </GoldButton>
            </div>
          </div>
        )}

        {/* Step 3: EFT Proof Upload */}
        {step === 3 && (
          <div className="space-y-5 animate-fade-in">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Upload EFT Proof of Payment</h3>
              <p className="text-sm text-muted-foreground">
                Transfer <span className="text-gold font-bold">R {(PLAN_PRICE[selectedPlan] ?? 1000).toLocaleString('en-ZA')}</span> to the MBG Nexus account and upload your proof below.
              </p>
            </div>

            {/* Bank Details */}
            <div className="p-4 rounded-2xl bg-surface-2 border border-border space-y-2">
              <p className="text-xs font-semibold text-gold uppercase tracking-wider mb-3">MBG Nexus Banking Details</p>
              {[
                { label: 'Bank', value: 'Discovery Bank' },
                { label: 'Account Holder', value: 'MBG NEXUS' },
                { label: 'Account Number', value: '15005319363' },
                { label: 'Branch Code', value: '679000' },
                { label: 'Reference', value: 'Your Full Name + ID Last 4' },
              ].map((detail) => (
                <div key={`bank-${detail.label}`} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{detail.label}</span>
                  <span className="text-foreground font-medium font-mono">{detail.value}</span>
                </div>
              ))}
            </div>

            {/* Upload Area */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => fileRef.current?.click()}
              className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
                dragOver ? 'border-gold bg-gold/10' : uploadedFile ? 'border-success bg-success/5' : 'border-border hover:border-gold/40 hover:bg-surface-2'
              }`}
            >
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) setUploadedFile(e.target.files[0]); }} />
              {uploadedFile ? (
                <div>
                  <Check className="w-10 h-10 text-success mx-auto mb-3" />
                  <p className="text-sm font-semibold text-success">{uploadedFile.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{(uploadedFile.size / 1024).toFixed(1)} KB — Click to replace</p>
                </div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm font-semibold text-foreground">Drag & drop or click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG — max 10MB (optional)</p>
                </div>
              )}
            </div>

            {!uploadedFile && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-info/10 border border-info/20">
                <AlertCircle className="w-4 h-4 text-info shrink-0 mt-0.5" />
                <p className="text-xs text-info/90">EFT proof is optional at registration. You can upload it later from your member portal. Your account will remain in <strong>Pending Payment</strong> status until confirmed by Finance.</p>
              </div>
            )}

            <div className="flex items-start gap-2">
              <input type="checkbox" id="agreeTerms" className="mt-0.5 accent-gold"
                {...register('agreeTerms', { required: 'You must agree to the terms' })} />
              <label htmlFor="agreeTerms" className="text-xs text-muted-foreground cursor-pointer">
                I agree to the <span className="text-gold hover:text-gold-light">Terms of Service</span> and{' '}
                <span className="text-gold hover:text-gold-light">Privacy Policy</span>. I understand my MBG username will be auto-generated and used for login. Participation is subject to applicable laws in my jurisdiction.
              </label>
            </div>
            {errors.agreeTerms && <p className="mt-1 text-xs text-danger">{errors.agreeTerms.message}</p>}

            <div className="flex gap-3">
              <GoldButton type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 justify-center">
                <ChevronLeft className="w-4 h-4" /> Back
              </GoldButton>
              <GoldButton type="submit" loading={isSubmitting} className="flex-1 justify-center">
                Submit Registration
              </GoldButton>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already a member?{' '}
        <button onClick={onSwitchToLogin} className="text-gold hover:text-gold-light font-semibold transition-colors">
          Sign In
        </button>
      </p>
    </div>
  );
}